import React, { Fragment, useEffect, useRef, useState } from "react";
import { wire } from "react-hot-wire";
import MaterialTable, { MTableToolbar, Query } from "material-table";
import { WithTranslation, withTranslation } from "react-i18next";
import { Category } from "models/prize/category/Category";
import Table, { Column, ColumnDefinition } from "models/common/Table";
import { CategoryService } from "../../../../services/prize/category/Category.service";
import Menu from "../../../common/menu/Menu";
import IconEdit from "@material-ui/icons/Edit";
import IconDelete from "@material-ui/icons/Delete";
import IconAdd from "@material-ui/icons/Add";
import { DrawerService } from "../../../../services/drawer/Drawer.service";

function CategoryList({
  categoryService,
  drawerService,
  t
}: {
  categoryService: CategoryService;
  drawerService: DrawerService;
} & WithTranslation) {
  const tableRef = useRef<any>(null);
  const refresh = () => tableRef.current.onQueryChange();
  const [supplierCategories, setSupplierCategories] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      const supplierCategoriesList = await categoryService.getSupplierCategories();
      setSupplierCategories(supplierCategoriesList);
    })();
  }, [categoryService]);

  const getRowMenuItems = (category: Category) => {
    const items = [];

    if (!category.parent) {
      items.push({
        icon: <IconAdd fontSize="small" className="mr-2" />,
        text: "add",
        onClick: () =>
          drawerService.activate("create-category", {
            parent: category,
            action: refresh
          })
      });
    }

    items.push({
      icon: <IconEdit fontSize="small" className="mr-2" />,
      text: "edit",
      onClick: () =>
        drawerService.activate("edit-category", {
          category,
          action: refresh
        })
    });

    items.push({
      icon: <IconDelete fontSize="small" className="mr-2" />,
      text: "delete",
      disabled: !category.parent && category.children.length > 0,
      onClick: () => {
        if (
          window.confirm(t("prize.category.list.actions.delete.confirmation"))
        ) {
          categoryService.delete(category).then(refresh);
        }
      }
    });

    return items;
  };

  const idToNameMap = new Map<string, string>();
  supplierCategories.forEach(c => {
    const id = `${c.supplierName}:${c.categoryId}`;
    const name = c.name.pl;
    idToNameMap.set(id, name);
  });

  return (
    <Fragment>
      <div className="m-3">
        <MaterialTable
          tableRef={tableRef}
          title={t("prize.category.list.title")}
          options={{ ...Table.options, loadingType: "overlay" }}
          columns={[
            ...([
              {
                title: t("prize.category.list.column.id"),
                field: "id",
                defaultSort: "desc"
              },
              {
                title: t("prize.category.list.column.name"),
                field: "name"
              },
              {
                title: t("prize.category.list.column.subcategory"),
                field: "subcategory"
              },
              {
                title: t("prize.category.list.column.supplier_categories"),
                field: "supplierCategories",
                render: (category: Category) =>
                  category.supplierCategories
                    .split(",")
                    .map(s => s.trim())
                    .filter(s => s)
                    .map(id => idToNameMap.get(id) || id)
                    .join(", ")
              },
              {
                title: t("prize.category.list.column.actions"),
                sorting: false,
                editable: "never",
                render: category =>
                  category && (
                    <Menu
                      translationPath="prize.category.list.rowMenu"
                      items={getRowMenuItems(category)}
                    />
                  )
              }
            ] as ColumnDefinition<Category>[]).map(
              column => new Column<Category>(column)
            )
          ]}
          data={async (query: Query<Category>) =>
            await categoryService.list(query)
          }
          components={{
            Toolbar: props => (
              <MTableToolbar {...props} search={!props.selectedRows.length} />
            )
          }}
          actions={[
            {
              icon: "add",
              tooltip: t("prize.category.list.actions.add.tooltip"),
              isFreeAction: true,
              onClick: () =>
                drawerService.activate("create-category", {
                  action: refresh
                })
            },
            {
              icon: "delete",
              tooltip: t("prize.category.list.actions.delete.tooltip"),
              onClick: () =>
                window.confirm(
                  t("prize.category.list.actions.delete.confirmation")
                )
            }
          ]}
          localization={t("prize.prize.list.localization", {
            returnObjects: true
          })}
        />
      </div>
    </Fragment>
  );
}

export default wire(
  ["categoryService", "drawerService"],
  withTranslation()(CategoryList)
);
